import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { hasAccess, canSeeMetrics, canAuthorArticles, ROLE_LABELS } from "@/lib/utils2";
import {
  LayoutDashboard, Radio, Newspaper, LifeBuoy, MessageSquareHeart,
  BarChart3, Users, Bell, LogOut, Wheat, Menu, X, StickyNote, Inbox as InboxIcon,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import AudioPlayer from "@/components/AudioPlayer";
import Footer from "@/components/Footer";

function NavItem({ to, icon: Icon, label, testid }) {
  return (
    <NavLink
      to={to}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
          isActive
            ? "bg-brand text-white"
            : "text-brand-ink hover:bg-white/70"
        }`
      }
    >
      <Icon className="w-[18px] h-[18px]" />
      {label}
    </NavLink>
  );
}

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  const loadNotifs = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data.notifications);
      setUnread(data.unread);
    } catch {}
  };

  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [user]);

  const markRead = async () => {
    try {
      await api.post("/notifications/read");
      setUnread(0);
      loadNotifs();
    } catch {}
  };

  const showNotifBell = true;

  const nav = (
    <nav className="space-y-1">
      <NavItem to="/app" icon={LayoutDashboard} label="Dashboard" testid="nav-dashboard" />
      <NavItem to="/app/podcasts" icon={Radio} label="Podcasts" testid="nav-podcasts" />
      <NavItem to="/app/notes" icon={StickyNote} label="My Notes" testid="nav-notes" />
      <NavItem to="/app/articles" icon={Newspaper} label="Articles" testid="nav-articles" />
      <NavItem to="/app/feedback" icon={MessageSquareHeart} label="Feedback" testid="nav-feedback" />
      <NavItem to="/app/help-desk" icon={LifeBuoy} label="Help Desk" testid="nav-helpdesk" />
      <NavItem to="/app/inbox" icon={InboxIcon} label="Notifications" testid="nav-inbox" />
      {canSeeMetrics(user) && <NavItem to="/app/metrics" icon={BarChart3} label="Metrics" testid="nav-metrics" />}
      {hasAccess(user, "admin") && <NavItem to="/app/users" icon={Users} label="User Management" testid="nav-users" />}
    </nav>
  );

  return (
    <div className="min-h-screen flex bg-brand-page">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-brand-sidebar border-r border-brand-line fixed inset-y-0">
        <div className="px-6 py-6 flex items-center gap-2.5 border-b border-brand-line">
          <div className="h-9 w-9 rounded-xl bg-brand flex items-center justify-center">
            <Wheat className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-display text-xl leading-none text-brand-ink">The Spread</p>
            <p className="text-[10px] uppercase tracking-widest text-brand-muted mt-0.5">Panera Coaching</p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-thin px-3 py-4">{nav}</div>
        <div className="px-3 py-4 border-t border-brand-line">
          <div className="px-3 py-2">
            <p className="text-sm font-semibold text-brand-ink truncate">{user.first_name} {user.last_name}</p>
            <p className="text-xs text-brand-muted">{ROLE_LABELS[user.role]} · Store {user.store_number}</p>
          </div>
          <button
            data-testid="logout-button"
            onClick={() => { logout(); navigate("/"); }}
            className="mt-1 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-brand-terracotta hover:bg-white/70 transition-colors duration-200"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 bg-brand-sidebar border-r border-brand-line flex flex-col">
            <div className="px-6 py-6 flex items-center justify-between border-b border-brand-line">
              <span className="font-display text-xl">The Spread</span>
              <button onClick={() => setMobileOpen(false)} data-testid="close-mobile-nav"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 px-3 py-4" onClick={() => setMobileOpen(false)}>{nav}</div>
            <div className="px-3 py-4 border-t border-brand-line">
              <button onClick={() => { logout(); navigate("/"); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-brand-terracotta">
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 lg:ml-64 min-w-0 flex flex-col">
        <div className="flex items-center justify-between lg:justify-end gap-3 px-4 sm:px-6 lg:px-8 h-14 border-b border-brand-line bg-brand-page">
          <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="open-mobile-nav">
            <Menu className="w-6 h-6" />
          </button>
          {showNotifBell && (
            <DropdownMenu onOpenChange={(o) => o && markRead()}>
              <DropdownMenuTrigger asChild>
                <button data-testid="notifications-button" className="relative h-9 w-9 rounded-full border border-brand-line flex items-center justify-center hover:bg-brand-sidebar transition-colors duration-200">
                  <Bell className="w-[18px] h-[18px]" />
                  {unread > 0 && (
                    <span data-testid="notif-badge" className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-brand-terracotta text-white text-[10px] font-bold flex items-center justify-center">
                      {unread}
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto scrollbar-thin border-brand-line">
                <div className="px-3 py-2 flex items-center justify-between">
                  <span className="font-display text-lg">Notifications</span>
                  <button onClick={() => navigate("/app/inbox")} className="text-xs text-brand font-semibold hover:underline" data-testid="view-all-notifications">View all</button>
                </div>
                {notifs.length === 0 && <div className="px-3 py-6 text-sm text-brand-muted text-center">No notifications yet.</div>}
                {notifs.slice(0, 8).map((n) => (
                  <div key={n.id} onClick={() => n.link && navigate(n.link)} className="px-3 py-2.5 border-t border-brand-line hover:bg-brand-sidebar/50 cursor-pointer" data-testid="notif-item">
                    <p className="text-sm font-semibold text-brand-ink">{n.title}</p>
                    <p className="text-xs text-brand-muted">{n.message}</p>
                  </div>
                ))}
                {notifs.length > 8 && (
                  <div className="px-3 py-2 border-t border-brand-line text-center">
                    <button onClick={() => navigate("/app/inbox")} className="text-sm text-brand font-semibold hover:underline">See all {notifs.length} notifications</button>
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        <AudioPlayer />
        <main className="flex-1">{children}</main>
        <Footer />
      </div>
    </div>
  );
}
